import { DropdownSelect } from '@components/common';
import { useInventorySuppliers } from '@hooks/inventory';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets/outletSelectors';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Landmark,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ApAgingRow,
  ApAgingSummary,
  SupplierStatementRow,
  fetchApAgingReport,
  fetchSupplierStatement,
} from '../../../../services/accountsPayableService';
import { apiError } from '../../../../util/notify';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const BUCKET_COLORS: Record<string, string> = {
  '0-30': 'text-green-700 bg-green-50',
  '31-60': 'text-yellow-700 bg-yellow-50',
  '61-90': 'text-orange-700 bg-orange-50',
  '90+': 'text-red-700 bg-red-50',
};

// ─── Supplier Statement Panel ────────────────────────────────────────────────

interface StatementPanelProps {
  supplierId: string;
  supplierName: string;
  companyId: number;
  outletId: string | null;
  onClose: () => void;
}

const StatementPanel: React.FC<StatementPanelProps> = ({
  supplierId,
  supplierName,
  companyId,
  outletId,
  onClose,
}) => {
  const [rows, setRows] = useState<SupplierStatementRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadStatement = async () => {
      setIsLoading(true);
      try {
        const statementRows = await fetchSupplierStatement(
          companyId,
          outletId,
          supplierId,
        );
        if (isMounted) setRows(statementRows);
      } catch (err) {
        apiError(
          err instanceof Error ? err.message : 'Failed to load statement',
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadStatement();

    return () => {
      isMounted = false;
    };
  }, [companyId, outletId, supplierId]);

  const closingBalance = rows[rows.length - 1]?.balance ?? 0;

  return (
    <div className="overflow-hidden rounded-2xl border border-blue-200 bg-blue-50">
      <div className="flex items-center justify-between border-b border-blue-100 bg-blue-100 px-5 py-3">
        <div>
          <h4 className="text-sm font-semibold text-blue-900">
            {supplierName} — Statement
          </h4>
          <p className="text-xs text-blue-600">
            Balance:{' '}
            <span className="font-semibold">
              {closingBalance >= 0
                ? `${fmt(closingBalance)} DR (owe)`
                : `${fmt(Math.abs(closingBalance))} CR (overpaid)`}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-blue-700 hover:underline"
        >
          Close
        </button>
      </div>
      {isLoading ? (
        <div className="flex h-16 items-center justify-center text-sm text-blue-500">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="flex h-16 items-center justify-center text-sm text-blue-400">
          No transactions found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-blue-100">
              <tr>
                {[
                  'Date',
                  'Reference',
                  'Description',
                  'Invoice',
                  'Payment',
                  'Balance',
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2 text-left text-xs font-semibold text-blue-800"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-50">
              {rows.map((r) => (
                <tr
                  key={`${r.type}-${r.date}-${r.reference}-${r.debit}-${r.credit}`}
                  className={r.type === 'payment' ? 'bg-white/50' : ''}
                >
                  <td className="px-4 py-1.5 text-gray-700">
                    {fmtDate(r.date)}
                  </td>
                  <td className="px-4 py-1.5 font-mono text-xs text-gray-500">
                    {r.reference || '—'}
                  </td>
                  <td className="px-4 py-1.5 text-gray-700">{r.description}</td>
                  <td className="px-4 py-1.5 text-right text-gray-900">
                    {r.debit > 0 ? fmt(r.debit) : '—'}
                  </td>
                  <td className="px-4 py-1.5 text-right text-green-700">
                    {r.credit > 0 ? fmt(r.credit) : '—'}
                  </td>
                  <td
                    className={`px-4 py-1.5 text-right font-semibold ${r.balance >= 0 ? 'text-gray-900' : 'text-green-700'}`}
                  >
                    {fmt(Math.abs(r.balance))}{' '}
                    <span className="text-xs font-normal text-gray-400">
                      {r.balance >= 0 ? 'DR' : 'CR'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── AP Summary Cards ─────────────────────────────────────────────────────────

const SummaryCards: React.FC<{ summary: ApAgingSummary }> = ({ summary }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
    {[
      {
        label: 'Total Outstanding',
        value: fmt(summary.totalOutstanding),
        sub: `${summary.supplierCount} supplier(s)`,
        color: 'text-gray-900',
        Icon: Landmark,
      },
      {
        label: '0–30 Days',
        value: fmt(summary.current),
        sub: 'current',
        color: 'text-green-700',
        Icon: Clock,
      },
      {
        label: '31–60 Days',
        value: fmt(summary.days31to60),
        sub: 'overdue',
        color: 'text-yellow-700',
        Icon: Clock,
      },
      {
        label: '61–90 Days',
        value: fmt(summary.days61to90),
        sub: 'overdue',
        color: 'text-orange-700',
        Icon: AlertCircle,
      },
      {
        label: '90+ Days',
        value: fmt(summary.days90plus),
        sub: 'severely overdue',
        color: 'text-red-700',
        Icon: AlertCircle,
      },
    ].map(({ label, value, sub, color, Icon }) => (
      <div
        key={label}
        className="rounded-2xl border border-gray-200 bg-white p-4"
      >
        <div className="flex items-start justify-between">
          <p className="text-xs text-gray-500">{label}</p>
          <Icon size={15} className={color} />
        </div>
        <p className={`mt-1 text-lg font-semibold ${color}`}>{value}</p>
        <p className="mt-0.5 text-xs text-gray-400">{sub}</p>
      </div>
    ))}
  </div>
);

// ─── AP Aging Table ───────────────────────────────────────────────────────────

interface AgingTableProps {
  rows: ApAgingRow[];
  onSelectSupplier: (id: string, name: string) => void;
  selectedSupplierId: string | null;
}

interface SortIconProps {
  col: keyof ApAgingRow;
  sortKey: keyof ApAgingRow;
  sortDir: 'asc' | 'desc';
}

const SortIcon: React.FC<SortIconProps> = ({ col, sortKey, sortDir }) =>
  sortKey === col ? (
    sortDir === 'asc' ? (
      <ChevronUp size={12} />
    ) : (
      <ChevronDown size={12} />
    )
  ) : null;

const AgingTable: React.FC<AgingTableProps> = ({
  rows,
  onSelectSupplier,
  selectedSupplierId,
}) => {
  const [sortKey, setSortKey] = useState<keyof ApAgingRow>('days_outstanding');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const toggleSort = (key: keyof ApAgingRow) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  if (rows.length === 0) {
    return (
      <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white text-center">
        <FileText size={28} className="text-gray-300" />
        <p className="text-sm text-gray-400">No outstanding payables found</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-5 py-3">
        <h4 className="text-sm font-semibold text-gray-900">
          Outstanding Invoices ({rows.length})
        </h4>
        <p className="text-xs text-gray-400">
          Click a supplier name to view their full statement
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-100 bg-gray-50">
            <tr>
              {(
                [
                  ['supplier_name', 'Supplier'],
                  ['invoice_number', 'Invoice #'],
                  ['invoice_date', 'Invoice Date'],
                  ['due_date', 'Due Date'],
                  ['total_amount', 'Total'],
                  ['amount_paid', 'Paid'],
                  ['outstanding', 'Outstanding'],
                  ['days_outstanding', 'Days'],
                  ['bucket', 'Age'],
                ] as [keyof ApAgingRow, string][]
              ).map(([key, label]) => (
                <th
                  key={key}
                  className="cursor-pointer px-4 py-2.5 text-left text-xs font-semibold text-gray-500 hover:text-gray-900"
                  onClick={() => toggleSort(key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {label}
                    <SortIcon col={key} sortKey={sortKey} sortDir={sortDir} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sorted.map((row) => (
              <tr
                key={row.invoice_id}
                className={`hover:bg-gray-50/50 ${selectedSupplierId === row.supplier_id ? 'bg-blue-50/30' : ''}`}
              >
                <td className="px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() =>
                      onSelectSupplier(row.supplier_id, row.supplier_name)
                    }
                    className="font-medium text-pos-primary hover:underline"
                  >
                    {row.supplier_name}
                  </button>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                  {row.invoice_number ?? '—'}
                </td>
                <td className="px-4 py-2.5 text-gray-600">
                  {fmtDate(row.invoice_date)}
                </td>
                <td className="px-4 py-2.5 text-gray-600">
                  {row.due_date ? fmtDate(row.due_date) : '—'}
                </td>
                <td className="px-4 py-2.5 text-right text-gray-900">
                  {fmt(row.total_amount)}
                </td>
                <td className="px-4 py-2.5 text-right text-green-700">
                  {row.amount_paid > 0 ? fmt(row.amount_paid) : '—'}
                </td>
                <td className="px-4 py-2.5 text-right font-semibold text-gray-900">
                  {fmt(row.outstanding)}
                </td>
                <td className="px-4 py-2.5 text-right text-gray-600">
                  {row.days_outstanding}d
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${BUCKET_COLORS[row.bucket]}`}
                  >
                    {row.bucket}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const AccountsPayablePage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const [rows, setRows] = useState<ApAgingRow[]>([]);
  const [summary, setSummary] = useState<ApAgingSummary>({
    totalOutstanding: 0,
    current: 0,
    days31to60: 0,
    days61to90: 0,
    days90plus: 0,
    supplierCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const suppliersQuery = useInventorySuppliers({
    companyId: companyId ?? 0,
    status: 'active',
    page: 1,
    pageSize: 200,
  });
  const suppliers = suppliersQuery.data?.data ?? [];

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const result = await fetchApAgingReport(
        companyId,
        outletId,
        supplierFilter === 'all' ? null : supplierFilter,
      );
      setRows(result.rows);
      setSummary(result.summary);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load payables');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, outletId, supplierFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Accounts Payable
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Outstanding supplier invoices aged by due date. Click a supplier to
            view their full statement.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DropdownSelect
            value={supplierFilter}
            placeholder="All suppliers"
            searchable
            options={[
              { value: 'all', label: 'All suppliers' },
              ...suppliers.map((s) => ({ value: s.id, label: s.name })),
            ]}
            onChange={(v) => {
              setSupplierFilter(v);
              setSelectedSupplier(null);
            }}
          />
        </div>
      </div>

      {/* Summary */}
      <SummaryCards summary={summary} />

      {/* Aging table */}
      {isLoading ? (
        <div className="flex h-32 items-center justify-center rounded-2xl border border-gray-200 bg-white text-sm text-gray-400">
          Loading…
        </div>
      ) : (
        <AgingTable
          rows={rows}
          onSelectSupplier={(id, name) =>
            setSelectedSupplier(
              selectedSupplier?.id === id ? null : { id, name },
            )
          }
          selectedSupplierId={selectedSupplier?.id ?? null}
        />
      )}

      {/* Supplier statement */}
      {selectedSupplier && companyId && (
        <StatementPanel
          supplierId={selectedSupplier.id}
          supplierName={selectedSupplier.name}
          companyId={companyId}
          outletId={outletId}
          onClose={() => setSelectedSupplier(null)}
        />
      )}
    </div>
  );
};
